import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare, Search, BarChart3, TrendingUp, TrendingDown, ArrowRight,
  Star, LineChart, Briefcase, ListTodo, Users, Zap, FileCheck2, UserPlus, Wallet,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useAuthStore } from '../../state/authStore';
import { useWatchlistStore } from '../../state/watchlistStore';
import { usePortfolioStore } from '../../state/portfolioStore';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function formatCurrency(val) {
  if (!val && val !== 0) return '--';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(val);
}

// Compact INR formatter for headline KPIs — e.g. 1.2Cr, 45.0L.
function formatCompactINR(val) {
  if (!val && val !== 0) return '--';
  const abs = Math.abs(val);
  if (abs >= 1_00_00_000) return `₹${(val / 1_00_00_000).toFixed(val >= 1e9 ? 0 : 1)} Cr`;
  if (abs >= 1_00_000) return `₹${(val / 1_00_000).toFixed(1)} L`;
  if (abs >= 1_000) return `₹${(val / 1_000).toFixed(1)}K`;
  return `₹${Math.round(val)}`;
}

function MarketIndexCard({ symbol, data, unavailable }) {
  if (unavailable && !data) {
    return (
      <Card className="bg-card border-border/50">
        <CardContent className="p-4">
          <div className="text-xs font-semibold text-muted-foreground mb-2">{symbol}</div>
          <p className="text-xs text-muted-foreground">Unavailable</p>
        </CardContent>
      </Card>
    );
  }
  if (!data) {
    return (
      <Card className="bg-card border-border/50">
        <CardContent className="p-4">
          <div className="text-xs font-semibold text-muted-foreground mb-2">{symbol}</div>
          <div className="h-6 bg-secondary/50 rounded animate-pulse" />
        </CardContent>
      </Card>
    );
  }
  const isPositive = data.price_change >= 0;
  return (
    <Card className="bg-card border-border/50 hover:border-border transition-colors">
      <CardContent className="p-4">
        <div className="text-xs font-semibold text-muted-foreground mb-1">{symbol}</div>
        <div className="flex items-end justify-between">
          <span className="text-lg font-bold text-foreground">{Number(data.current_price).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
          <span className={`flex items-center gap-0.5 text-xs font-semibold ${isPositive ? 'text-emerald-500' : 'text-red-500'}`}>
            {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {isPositive ? '+' : ''}{Number(data.price_change_pct).toFixed(2)}%
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function QuickActionCard({ icon: Icon, title, description, href, gradient }) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(href)}
      className={`group relative overflow-hidden rounded-xl border border-border/50 p-5 text-left transition-all hover:border-emerald-500/30 hover:shadow-lg hover:shadow-emerald-500/5 ${gradient}`}
    >
      <div className="relative z-10">
        <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-3 group-hover:bg-emerald-500/20 transition-colors">
          <Icon className="w-[18px] h-[18px] text-emerald-600 dark:text-emerald-400" />
        </div>
        <h3 className="text-sm font-semibold text-foreground mb-0.5">{title}</h3>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <ArrowRight className="absolute bottom-4 right-4 w-4 h-4 text-muted-foreground/30 group-hover:text-emerald-500 group-hover:translate-x-0.5 transition-all" />
    </button>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isInstitutional = user?.userType === 'institutional';
  const watchlist = useWatchlistStore((s) => s.watchlist);
  const loadWatchlist = useWatchlistStore((s) => s.loadWatchlist);
  const getPortfolioStats = usePortfolioStore((s) => s.getPortfolioStats);
  const holdings = usePortfolioStore((s) => s.holdings);
  const portfolioStats = getPortfolioStats();
  const [topTickerPrices, setTopTickerPrices] = useState({});
  const [marketPricesError, setMarketPricesError] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [taskOrg, setTaskOrg] = useState('');
  const [tasks, setTasks] = useState([]);

  // Institutional-only state. Kept here (not in an advisor store) to avoid
  // pulling in advisorStore as a hard dependency on the retail home page.
  const [clientSummary, setClientSummary] = useState({ items: [], total: 0, aum: 0 });
  const [pendingApprovals, setPendingApprovals] = useState([]);

  useEffect(() => {
    if (!user?.id) return;
    axios.get(`${API}/orgs`).then(({ data }) => {
      const o = Array.isArray(data) ? data : [];
      setOrgs(o);
      if (o[0]?.id) setTaskOrg(o[0].id);
    }).catch(() => {});
  }, [user?.id]);

  useEffect(() => {
    if (!taskOrg) return;
    axios.get(`${API}/signal-tasks`, { params: { org_id: taskOrg } }).then(({ data }) => {
      setTasks(Array.isArray(data) ? data : []);
    }).catch(() => setTasks([]));
  }, [taskOrg]);

  // Institutional: load recent clients + pending approvals once we know the
  // user is an advisor. Active org scope is optional; if none is set we still
  // fetch personal clients.
  useEffect(() => {
    if (!isInstitutional || !user?.id) return;
    const params = { page: 1, page_size: 5 };
    if (taskOrg) params.org_id = taskOrg;
    axios.get(`${API}/advisor/clients`, { params }).then(({ data }) => {
      const items = Array.isArray(data?.items) ? data.items : [];
      const total = Number(data?.total) || items.length;
      const aum = items.reduce((sum, c) => sum + (Number(c.corpus) || 0), 0);
      setClientSummary({ items, total, aum });
    }).catch(() => setClientSummary({ items: [], total: 0, aum: 0 }));
  }, [isInstitutional, user?.id, taskOrg]);

  useEffect(() => {
    if (!isInstitutional || !taskOrg) { setPendingApprovals([]); return; }
    axios.get(`${API}/advisor/proposals/pending`, { params: { org_id: taskOrg } })
      .then(({ data }) => setPendingApprovals(Array.isArray(data) ? data : []))
      .catch(() => setPendingApprovals([]));
  }, [isInstitutional, taskOrg]);

  // Retail-only: personal watchlist is not shown on the advisor home page.
  useEffect(() => {
    if (isInstitutional) return;
    loadWatchlist();
  }, [isInstitutional, loadWatchlist]);

  // Market-overview prices are shown for both personas.
  useEffect(() => {
    let cancelled = false;
    let timeoutId;
    let consecutiveFailures = 0;
    const INITIAL_GRACE_FAILURES = 3;

    const scheduleNext = (ms) => {
      if (cancelled) return;
      timeoutId = setTimeout(fetchPrices, ms);
    };

    const fetchPrices = async () => {
      try {
        const response = await axios.get(`${API}/top-tickers-prices`, { timeout: 8_000 });
        if (cancelled) return;
        if (response.data?.data) {
          setTopTickerPrices(response.data.data);
          setMarketPricesError(null);
          consecutiveFailures = 0;
          scheduleNext(60_000);
          return;
        }
        throw new Error('empty payload');
      } catch {
        if (cancelled) return;
        consecutiveFailures += 1;
        if (consecutiveFailures > INITIAL_GRACE_FAILURES) {
          setMarketPricesError('Market data temporarily unavailable.');
        }
        scheduleNext(consecutiveFailures < 5 ? 4_000 : 15_000);
      }
    };

    fetchPrices();
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const indices = ['NIFTY 50', 'SENSEX', 'NIFTYBEES'];
  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className="space-y-4">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold text-foreground tracking-tight">
          {getGreeting()}, {user?.name?.split(' ')[0] || 'there'}
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">{today}</p>
      </div>

      {/* Market Overview */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-foreground">Market Overview</h2>
        </div>
        {marketPricesError && (
          <p className="text-xs text-amber-600 dark:text-amber-500 mb-2">{marketPricesError}</p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {indices.map((sym) => (
            <MarketIndexCard key={sym} symbol={sym} data={topTickerPrices[sym]} unavailable={!!marketPricesError} />
          ))}
        </div>
      </div>

      {/* Advisor KPI strip — institutional only */}
      {isInstitutional && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground mb-2">
                <Users className="w-3.5 h-3.5 text-emerald-500" /> Clients
              </div>
              <p className="text-2xl font-bold text-foreground">{clientSummary.total}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {taskOrg ? 'In the selected organization' : 'Across your books'}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground mb-2">
                <Wallet className="w-3.5 h-3.5 text-emerald-500" /> Indicative AUM
              </div>
              <p className="text-2xl font-bold text-foreground">{formatCompactINR(clientSummary.aum)}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                From stated client corpora (top {clientSummary.items.length} shown)
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground mb-2">
                <FileCheck2 className="w-3.5 h-3.5 text-emerald-500" /> Pending approvals
              </div>
              <p className="text-2xl font-bold text-foreground">{pendingApprovals.length}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {pendingApprovals.length ? 'Awaiting compliance review' : 'Nothing in the queue'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Actions */}
      <div>
        <h2 className="text-sm font-semibold text-foreground mb-3">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <QuickActionCard
            icon={MessageSquare}
            title="Ask KubAIr"
            description={isInstitutional
              ? 'Market, sector & client-ready analysis'
              : 'Portfolio ideas, stock insights & allocation help'}
            href="/public/ai"
            gradient="bg-card"
          />
          {isInstitutional ? (
            <>
              <QuickActionCard
                icon={UserPlus}
                title="Add a client"
                description="Onboard a client and set their risk profile"
                href="/public/clients"
                gradient="bg-card"
              />
              <QuickActionCard
                icon={Zap}
                title="Signals"
                description="Today's AI signals and assigned follow-ups"
                href="/public/signals"
                gradient="bg-card"
              />
              <QuickActionCard
                icon={Search}
                title="Research"
                description="Screener, compare, scoring and debt monitor"
                href="/public/screener"
                gradient="bg-card"
              />
            </>
          ) : (
            <>
              <QuickActionCard
                icon={Search}
                title="Find Stocks with AI"
                description="Natural language stock screener"
                href="/public/find-stocks"
                gradient="bg-card"
              />
              <QuickActionCard
                icon={BarChart3}
                title="Explore Stocks"
                description="Browse 5000+ listed Indian securities"
                href="/public/screener"
                gradient="bg-card"
              />
              <QuickActionCard
                icon={ListTodo}
                title="Signal tasks"
                description="Follow-ups assigned from Signal Center"
                href="/public/signals"
                gradient="bg-card"
              />
            </>
          )}
        </div>
      </div>

      {orgs.length > 0 && (
        <Card className="bg-card border-border/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <ListTodo className="w-4 h-4 text-emerald-500" /> Open signal tasks
              </h3>
              <select className="text-xs rounded border bg-background px-2 py-1" value={taskOrg} onChange={e => setTaskOrg(e.target.value)}>
                {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div className="space-y-1 text-xs max-h-40 overflow-y-auto">
              {tasks.filter(t => t.status === 'open' || t.status === 'in_progress').slice(0, 8).map(t => (
                <div key={t.id} className="flex justify-between gap-2 border-b border-border/30 py-1">
                  <span className="truncate">{t.symbol || t.isin || t.signal_type}</span>
                  <span className="text-muted-foreground shrink-0">{t.status}</span>
                </div>
              ))}
              {tasks.length === 0 && <p className="text-muted-foreground">No tasks.</p>}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main content — role-aware */}
      {isInstitutional ? (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Recent Clients */}
          <Card className="lg:col-span-3 bg-card border-border/50">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-emerald-500" />
                  <h3 className="text-sm font-semibold text-foreground">Recent clients</h3>
                  {clientSummary.total > 0 && (
                    <span className="text-xs text-muted-foreground">{clientSummary.total} total</span>
                  )}
                </div>
                <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => navigate('/public/clients')}>
                  Manage <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
              {clientSummary.items.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground mb-3">No clients yet</p>
                  <Button size="sm" variant="outline" onClick={() => navigate('/public/clients')} className="text-xs">
                    Add your first client
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {clientSummary.items.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => navigate(`/public/clients/${c.id}`)}
                      className="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-secondary/50 transition-colors text-left"
                    >
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-foreground truncate block">{c.name || 'Unnamed client'}</span>
                        <span className="text-xs text-muted-foreground truncate block">
                          {[
                            c.risk_bucket,
                            c.investment_goal,
                            c.holdings_count != null ? `${c.holdings_count} holdings` : null,
                          ].filter(Boolean).join(' · ')}
                        </span>
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        <span className="text-xs font-medium text-muted-foreground">
                          {c.corpus ? formatCompactINR(c.corpus) : '--'}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pending approvals */}
          <Card className="lg:col-span-2 bg-card border-border/50">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FileCheck2 className="w-4 h-4 text-emerald-500" />
                  <h3 className="text-sm font-semibold text-foreground">Pending approvals</h3>
                </div>
                {pendingApprovals.length > 0 && (
                  <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => navigate('/public/clients')}>
                    Review <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                )}
              </div>
              {pendingApprovals.length === 0 ? (
                <div className="text-center py-6">
                  <FileCheck2 className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">
                    {taskOrg ? 'No proposals awaiting approval.' : 'Join an organization to see its approval queue.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {pendingApprovals.slice(0, 6).map((p) => (
                    <button
                      key={p.id}
                      onClick={() => navigate(`/public/clients/${p.client_id}`)}
                      className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-secondary/50 transition-colors text-left"
                    >
                      <div className="min-w-0">
                        <span className="text-xs font-medium text-foreground truncate block">
                          {p.client_name || 'Proposal'}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {p.approval_status || 'pending_review'}
                        </span>
                      </div>
                      <ArrowRight className="w-3 h-3 text-muted-foreground shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Watchlist Snapshot */}
          <Card className="lg:col-span-3 bg-card border-border/50">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-emerald-500" />
                  <h3 className="text-sm font-semibold text-foreground">Watchlist</h3>
                  {watchlist.length > 0 && (
                    <span className="text-xs text-muted-foreground">{watchlist.length} stocks</span>
                  )}
                </div>
                {watchlist.length > 0 && (
                  <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => navigate('/public/watchlist')}>
                    View all <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                )}
              </div>
              {watchlist.length === 0 ? (
                <div className="text-center py-8">
                  <Star className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground mb-3">No stocks in your watchlist yet</p>
                  <Button size="sm" variant="outline" onClick={() => navigate('/public/screener')} className="text-xs">
                    Browse Explorer
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {watchlist.slice(0, 5).map((item) => (
                    <button
                      key={item.id || item.symbol}
                      onClick={() => navigate(`/public/india-company/${item.symbol}`)}
                      className="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-secondary/50 transition-colors text-left"
                    >
                      <div>
                        <span className="text-sm font-medium text-foreground">{item.companyName || item.symbol}</span>
                        <span className="text-xs text-muted-foreground ml-2">{item.symbol}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-medium text-muted-foreground">{item.exchange || 'NSE'}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Portfolio Summary */}
          <Card className="lg:col-span-2 bg-card border-border/50">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-emerald-500" />
                  <h3 className="text-sm font-semibold text-foreground">Portfolio</h3>
                </div>
                {holdings.length > 0 && (
                  <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => navigate('/public/portfolio')}>
                    View <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                )}
              </div>
              {holdings.length === 0 ? (
                <div className="text-center py-8">
                  <LineChart className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground mb-3">Set up your portfolio</p>
                  <Button size="sm" variant="outline" onClick={() => navigate('/public/portfolio/add-holding')} className="text-xs">
                    Add Holding
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Total Value</p>
                    <p className="text-xl font-bold text-foreground">{formatCurrency(portfolioStats.totalValue)}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground">Invested</p>
                      <p className="text-sm font-semibold text-foreground">{formatCurrency(portfolioStats.totalInvested)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">P&L</p>
                      <p className={`text-sm font-semibold ${portfolioStats.totalPnL >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {portfolioStats.totalPnL >= 0 ? '+' : ''}{formatCurrency(portfolioStats.totalPnL)}
                        <span className="text-xs ml-1">({portfolioStats.totalPnLPercent?.toFixed(1)}%)</span>
                      </p>
                    </div>
                  </div>
                  {portfolioStats.topHoldings?.length > 0 && (
                    <div className="pt-2 border-t border-border/50">
                      <p className="text-xs text-muted-foreground mb-2">Top Holdings</p>
                      <div className="space-y-1.5">
                        {portfolioStats.topHoldings.slice(0, 3).map((h) => (
                          <div key={h.symbol} className="flex items-center justify-between text-xs">
                            <span className="font-medium text-foreground">{h.companyName || h.symbol}</span>
                            <span className={`font-semibold ${h.unrealizedPnL >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                              {h.unrealizedPnL >= 0 ? '+' : ''}{h.unrealizedPnLPercent?.toFixed(1)}%
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
